import React, { useState, useEffect, useCallback } from "react";
import PageTabs from '../shared/PageTabs';
import { USERS_TABS } from '../shared/tabConfigs';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip, TextField, InputAdornment, MenuItem, Select, FormControl, Checkbox, Menu, ListItemIcon, ListItemText
} from "@mui/material";
import {
  Delete, Download, Search, Clear, Add, Edit, Add as AddIcon, Work as WorkIcon, FilterList as FilterListIcon
} from "@mui/icons-material";
import { jobRoleService } from "../../services/api";
import { useToast } from "../../context/ToastContext";
import JobRoleForm from "./JobRoleForm";
import ConfirmDialog from "../common/ConfirmDialog";
import { formatDate } from '../../utils/dateFormatter';
import EmptyState from '../common/EmptyState';
const JobRoles = () => {
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalJobRoles, setTotalJobRoles] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(''); // Add status filter state
  const [filterType, setFilterType] = useState("0");
  const [sortBy, setSortBy] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const { showToast } = useToast();
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
  });
  const [selectedJobRoles, setSelectedJobRoles] = useState([]);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [selectedJobRole, setSelectedJobRole] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  // Add state for Actions button menu
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);

  // Add state for Add button menu
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const isAddMenuOpen = Boolean(addMenuAnchorEl);

  const fetchJobRoles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobRoleService.getJobRoles(
        page + 1,
        rowsPerPage,
        searchTerm,
        statusFilter,
        sortBy
      );
      console.log("Job Roles Response:", response.data); // Debugging log

      if (response.data.success) {
        setJobRoles(response.data.jobRoles);
        setTotalJobRoles(response.data.totalJobRoles);
        setPermissions(response.data.permissions);
      }
    } catch (error) {
      console.error("Error fetching job roles:", error);
      showToast("Failed to load job roles", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm,statusFilter,sortBy, showToast]);

  useEffect(() => {
    fetchJobRoles();
  }, [fetchJobRoles]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

// Add handler for status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
        setExpandedRows({});
    };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPage(0);
  };

  const handleCreateJobRole = () => {
    setSelectedJobRole(null);
    setOpenCreateForm(true);
  };

  const handleEditJobRole = (jobRole) => {
    setSelectedJobRole(jobRole);
    setOpenEditForm(true);
  };

  // Actions button menu handlers
  const handleActionsMenuClick = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const handleAddMenuClick = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleBulkDelete = () => {
    handleActionsMenuClose();

    if (selectedJobRoles.length === 0) {
      showToast("Please select job roles to delete", "warning");
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Delete Job Roles',
      content: `Are you sure you want to delete ${selectedJobRoles.length} job role(s)? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await jobRoleService.bulkDeleteJobRoles(selectedJobRoles);
          if (response.data.success) {
            showToast(response.data.message, 'success');
            setSelectedJobRoles([]);
            fetchJobRoles();
          }
        } catch (error) {
          console.error('Error deleting job roles:', error);
          showToast('Error deleting job roles', 'error');
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
    setPage(0);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };
  const handleFormSubmit = () => {
    fetchJobRoles();
    setOpenCreateForm(false);
    setOpenEditForm(false);
  };
  return (
    <Box sx={{ p: 2 }}>
      <PageTabs tabs={USERS_TABS} />
      <Typography variant="h4" gutterBottom className="page-title">
        Job Roles
      </Typography>
      <Typography variant="body2" sx={{ mb: 4 }}>
        Manage job roles and their associated skill categories. Create, edit, and organize job roles for better user classification and skill mapping.
      </Typography>
      
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            placeholder="Job Roles"
            // className="custom-search-field"
            className="pink-filter"
            sx={{height: '35px'}}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {/* <FormControl variant="outlined" size="small">
            <Select
              value={filterType}
              onChange={handleFilterChange}
              displayEmpty
            >
              <MenuItem value="0">All Status</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Inactive</MenuItem>
            </Select>
          </FormControl> */}
           <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                                  <Select
                                      value={statusFilter}
                                      className="pink-filter"
                                      onChange={handleStatusFilterChange}
                                      displayEmpty
                                      inputProps={{ 'aria-label': 'Status filter' }}
                                      startAdornment={
                                          <InputAdornment position="end">
                                              <FilterListIcon fontSize="small" />
                                          </InputAdornment>
                                      }
                                  >
                                      <MenuItem value="">All Status</MenuItem>
                                      <MenuItem value="active">Active</MenuItem>
                                      <MenuItem value="inactive">Inactive</MenuItem>
                                  </Select>
                              </FormControl>

          <FormControl  size="small">
            <Select
              displayEmpty
              value={sortBy}
              className="pink-filter"
              onChange={handleSortChange}
            >
              <MenuItem value="">Default</MenuItem>
              <MenuItem value="name asc">Name A-Z</MenuItem>
              <MenuItem value="name desc">Name Z-A</MenuItem>
              <MenuItem value="created_at asc">Oldest First</MenuItem>
              <MenuItem value="created_at desc">Newest First</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          {selectedJobRoles.length > 0 && (
            <Button
              variant="outlined"
              onClick={handleActionsMenuClick}
              aria-controls={isActionsMenuOpen ? "actions-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isActionsMenuOpen ? "true" : undefined}
              sx={{ mr: 1 }}
            >
              Actions
            </Button>
          )}
          <Menu
            id="actions-menu"
            anchorEl={actionsMenuAnchorEl}
            open={isActionsMenuOpen}
            onClose={handleActionsMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {permissions.canDelete && (
              <MenuItem onClick={handleBulkDelete}>
                <Delete fontSize="small" sx={{ mr: 1 }} />
                Delete Selected
              </MenuItem>
            )}
          </Menu>

          {permissions.canCreate && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateJobRole}
                aria-controls={isAddMenuOpen ? 'add-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isAddMenuOpen ? 'true' : undefined}
              >
                Add Job Role
              </Button>

              {/* <Menu
                id="add-menu"
                anchorEl={addMenuAnchorEl}
                open={isAddMenuOpen}
                onClose={handleAddMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'add-button',
                }}
              >
                <MenuItem onClick={() => {
                  handleAddMenuClose();
                  handleCreateJobRole();
                }}>
                  <ListItemIcon>
                    <WorkIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Add Job Role</ListItemText>
                </MenuItem>
              </Menu> */}
            </>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedJobRoles.length > 0 &&
                    selectedJobRoles.length < jobRoles.length
                  }
                  checked={
                    jobRoles.length > 0 && selectedJobRoles.length === jobRoles.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedJobRoles(jobRoles.map((jr) => jr.id));
                    } else {
                      setSelectedJobRoles([]);
                    }
                  }}
                />
              </TableCell>
              <TableCell>Role Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : jobRoles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                  <EmptyState framed={false} title="No job roles found" message={null} />
                </TableCell>
              </TableRow>
            ) : (
              jobRoles.map((jobRole) => (
                <TableRow
                  key={jobRole.id}
                  hover
                  selected={selectedJobRoles.includes(jobRole.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedJobRoles.includes(jobRole.id)}
                      onChange={() => {
                        if (selectedJobRoles.includes(jobRole.id)) {
                          setSelectedJobRoles(
                            selectedJobRoles.filter((id) => id !== jobRole.id)
                          );
                        } else {
                          setSelectedJobRoles([...selectedJobRoles, jobRole.id]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {permissions.canEdit ? (
                      <Box
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                        }}
                        onClick={() => handleEditJobRole(jobRole)}
                      >
                        {jobRole.role_name}
                      </Box>
                    ) : (
                      <Box>{jobRole.role_name}</Box>
                    )}
                  </TableCell>
                  <TableCell>{jobRole.category_names || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={jobRole.status === 1 ? 'Active' : 'Inactive'}
                      color={jobRole.status === 1 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>                    
                    {formatDate(jobRole.created_at)}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {permissions.canEdit && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditJobRole(jobRole)}
                          title="Edit Job Role"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalJobRoles}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {openCreateForm && (
        <JobRoleForm
          open={openCreateForm}
          onClose={() => setOpenCreateForm(false)}
          onSubmit={handleFormSubmit}
        />
      )}

      {openEditForm && selectedJobRole && (
        <JobRoleForm
          open={openEditForm}
          onClose={() => setOpenEditForm(false)}
          onSubmit={handleFormSubmit}
          jobRole={selectedJobRole}
        />
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
    </Box>
  );
};

export default JobRoles;